import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import MicSetupStep from "@/components/recstudio/setup/MicSetupStep";
import AudioTestStep from "@/components/recstudio/setup/AudioTestStep";
import useMicStream from "@/components/recstudio/setup/useMicStream";
import { levelAdvice } from "@/lib/audioAnalysis";
import { saveMic } from "@/lib/creatorVocal";
import { micPermission } from "@/lib/micHelp";
import { loadSetup, saveSetup } from "@/lib/recStudioApi";

const STEPS = ["Microphone", "Headphones & sync"];

/** Recording Setup Wizard — step 1 mic, step 2 headphones / test / sync → saves the setup and shows READY. */
export default function SetupWizard({ onDone }) {
  const saved = loadSetup();
  const mic = useMicStream(saved.deviceId);
  const [step, setStep] = useState(0);
  const [results, setResults] = useState({ noise: saved.noiseDb ?? null, level: saved.peakDb ?? null, headphones: saved.headphones ?? null, latencyMs: saved.latencyMs ?? null });
  // Already allowed before → open the mic straight away (no extra click).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { micPermission().then((s) => { if (s === "granted") mic.open(); }); }, []);
  const micReady = mic.state === "live" && levelAdvice(results.level)?.ok;
  const finish = () => {
    saveMic(mic.deviceId);
    const next = saveSetup({ deviceId: mic.deviceId, noiseDb: results.noise, peakDb: results.level, headphones: results.headphones === true, latencyMs: results.latencyMs, ready: true });
    mic.close();
    onDone(next);
  };
  return (
    <div data-testid="setup-wizard" className="mx-auto max-w-4xl">
      <ol className="mb-6 flex flex-wrap gap-2">
        {STEPS.map((s, i) => (
          <li key={s}>
            <button type="button" data-testid={`setup-step-${i + 1}`} aria-current={step === i ? "step" : undefined} onClick={() => setStep(i)}
              className={`rounded-full border px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.16em] transition-colors duration-200 ${step === i ? "border-rose-300/60 bg-rose-500/15 text-white" : "border-white/10 text-white/50 hover:text-white"}`}>
              {i + 1} · {s}
            </button>
          </li>
        ))}
      </ol>
      {step === 0 ? <MicSetupStep mic={mic} results={results} setResults={setResults} /> : <AudioTestStep mic={mic} results={results} setResults={setResults} />}
      <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
        <button type="button" data-testid="setup-back" onClick={() => (step ? setStep(0) : onDone(null))} className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white"><ArrowLeft className="h-4 w-4" /> {step ? "Back" : "Cancel"}</button>
        {step === 0 ? (
          <button type="button" data-testid="setup-next" onClick={() => setStep(1)} disabled={mic.state !== "live"} className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-display font-semibold text-[#05070f] disabled:opacity-40">
            Next: headphones <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button type="button" data-testid="setup-finish" onClick={finish} disabled={mic.state !== "live" || results.headphones == null}
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 px-6 py-3 font-display font-semibold text-[#05070f] disabled:opacity-40">
            <CheckCircle2 className="h-4 w-4" /> {micReady ? "I'm ready — start recording" : "Finish setup"}
          </button>
        )}
      </div>
    </div>
  );
}
